import axios from "axios";

const API = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

API.interceptors.response.use(
  (response) => response,
  (error) => {
    // A 401 from an already-authenticated request means the token expired or
    // was revoked server-side — clear it so the WebSocket provider (and any
    // other consumer reading localStorage) stops retrying with a dead token
    // instead of silently failing forever. Login's own 401 (wrong credentials)
    // is excluded — that's handled inline on the login page, not a session expiry.
    const isLoginRequest = error.config?.url?.includes("/auth/login");
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.clear();
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

// AUTH
export const login = (data) => API.post("/auth/login", data);

// FORGOT PASSWORD — Superadmin/Admin/Branch Manager login only. The three
// below are called with no JWT (the requester has no session yet); the
// admin-side list/approve/reject further down ARE authenticated.
export const requestPasswordReset = (username) => API.post("/auth/password-reset/request", { username });
export const getPasswordResetStatus = (token) => API.get(`/auth/password-reset/${token}/status`);
export const verifyPasswordResetCode = (token, code) =>
  API.post(`/auth/password-reset/${token}/verify-code`, { code });
export const setPasswordResetNewPassword = (token, new_password) =>
  API.post(`/auth/password-reset/${token}/set-password`, { new_password });

export const getPasswordResetRequests = () => API.get("/password-reset-requests");
export const approvePasswordResetRequest = (id) => API.patch(`/password-reset-requests/${id}/approve`);
export const rejectPasswordResetRequest = (id, reason) => API.patch(`/password-reset-requests/${id}/reject`, { reason });

// BRANCH APIs
export const getBranches = (params) => API.get("/branch", { params });
export const getBranchById = (id) => API.get(`/branch/${id}`);
export const createBranch = (data) => API.post("/branch", data);
export const updateBranch = (id, data) => API.put(`/branch/${id}`, data);
export const deleteBranch = (id) => API.delete(`/branch/${id}`);
export const toggleBranchRaining = (id, is_raining) => API.patch(`/branch/${id}/raining`, { is_raining });
// Customer-facing: check if a lat/lng falls inside any active branch's service area
// Response: { serviceable: bool, branch: { branch_id, branch_name, ... } | null }
export const checkBranchServiceArea = (lat, lng) =>
  API.get("/branch/check-location", { params: { lat, lng } });

// BRANCH MANAGER APIs
export const getBranchManagers = (params) => API.get("/branch/managers", { params });
export const getUnassignedBranchManagers = () => API.get("/branch/managers/unassigned");
export const getBranchManagerById = (id) => API.get(`/branch/managers/${id}`);
export const createBranchManager = (data) => API.post("/branch/managers", data);
export const updateBranchManager = (id, data) => API.put(`/branch/managers/${id}`, data);
export const toggleBranchManagerStatus = (id, status) => API.patch(`/branch/managers/${id}/status`, { status });
export const deleteBranchManager = (id) => API.delete(`/branch/managers/${id}`);
export const assignBranchManager = (branchId, managerUserId) => API.post(`/branch/${branchId}/assign-manager`, { manager_user_id: managerUserId });
export const unassignBranchManager = (branchId) => API.post(`/branch/${branchId}/unassign-manager`);

// CUSTOMER NOTIFY — ad-hoc push + in-app notification to customers, Super
// Admin only (see mahinzo_BE's dashboardController.sendCustomerNotification).
// Defaults to every active customer when target_users is omitted/empty.
export const sendCustomerNotification = (data) => API.post("/dashboard/send-customer-notification", data);

// CREDENTIAL MANAGEMENT — Super Admin only. Covers Admin, Branch Manager,
// Delivery Boy, QC Inspector, Purchase Person (role_id 2,3,6,7,8,10) — no
// vendor/customer coverage, this backend module doesn't manage those.
export const getCredentials = (params) => API.get("/credentials", { params });
export const getCredential = (userId) => API.get(`/credentials/${userId}`);
export const updateCredential = (userId, data) => API.patch(`/credentials/${userId}`, data);
export const updateCredentialPassword = (userId, new_password) =>
  API.patch(`/credentials/${userId}/password`, { new_password });
export const updateCredentialUsername = (userId, new_username) =>
  API.patch(`/credentials/${userId}/username`, { new_username });
export const getBranchCredentialSummary = (branchId) =>
  API.get(`/credentials/branch/${branchId}/summary`);


// FILE UPLOAD — folder maps to the upload type registered in
// uploadMiddleware.js's UPLOAD_CONFIG (e.g. "notification"), which decides
// the destination directory server-side; there's no single shared
// /upload/image route, every module has its own /upload/:type.
export const uploadImage = (file, folder = "notification") => {
  const fd = new FormData();
  fd.append("file", file);
  return API.post(`/dashboard/upload/${folder}`, fd);
};

// CATEGORY
export const getCategory = (params) => API.get("/category", { params });
export const getCategoryById = (id) => API.get(`/category/${id}`);
export const createCategory = (data) => API.put("/category", data);
export const updateCategory = (id, data) => API.put(`/category/${id}`, data);
export const uploadCategoryImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return API.post("/category/upload/category", fd);
};
export const deleteCategory = (id) => API.delete(`/category/${id}`);

// BANNER
export const getBanners = (params) => API.get("/banner/all", { params });
export const getBannerById = (id) => API.get(`/banner/${id}`);
export const createBanner = (data) => API.put("/banner", data);
export const updateBanner = (id, data) => API.put(`/banner/${id}`, data);
export const deleteBanner = (id) => API.delete(`/banner/${id}`);
export const uploadBannerImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return API.post("/banner/upload/banner", fd);
};

// CAMPAIGN / PROMOTION APIs — QR scratch-card campaigns.
export const getCampaigns = (params) => API.get("/promotions", { params });
export const getReferrals = () => API.get("/referrals");
export const getReferralSettings = () => API.get("/referrals/settings");
export const updateReferralSettings = (data) => API.put("/referrals/settings", data);
export const getCheckoutOffer = () => API.get("/checkout-offer");
export const updateCheckoutOffer = (data) => API.put("/checkout-offer", data);
export const getCampaignById = (id) => API.get(`/promotions/${id}`);
export const createCampaign = (data) => API.put("/promotions", data);
export const updateCampaign = (id, data) => API.put(`/promotions/${id}`, data);
export const deleteCampaign = (id) => API.delete(`/promotions/${id}`);
export const getCampaignAnalytics = (id) => API.get(`/promotions/${id}/analytics`);
export const getCampaignCoupons = (id, params) => API.get(`/promotions/${id}/coupons`, { params });
export const updateCouponGiftStatus = (couponId, data) => API.put(`/promotions/coupons/${couponId}/gift-status`, data);
export const getCampaignQr = (id, params) => API.get(`/promotions/${id}/qr`, { params });

// VARIETY (category-scoped tags — replaces the old similar-name variety-linking flow)
export const getVarietiesByCategory = (categoryId) =>
  API.get("/varieties", { params: { category_id: categoryId } });
export const createVarieties = (data) => API.put("/varieties", data); // { category_id, names: [...] }
export const deleteVariety = (id) => API.delete(`/varieties/${id}`);

// VENDOR APIs
export const getVendors = (params) => API.get("/vendors", { params });
export const getVendorById = (id) => API.get(`/vendors/${id}`);
export const createVendor = (data) => API.put("/vendors", data);
export const updateVendor = (id, data) => API.put(`/vendors/${id}`, data);
export const deleteVendor = (id) => API.delete(`/vendors/${id}`);
// Admin control of the vendor's shop open/closed switch (mirrors the vendor
// app's own self-service toggle) — { is_shop_open: true|false }.
export const updateVendorShopStatus = (id, data) => API.put(`/vendors/${id}/profile`, data);
// Bulk emergency/holiday switch — closes/opens every vendor at once
// (optionally scoped by branch_id and business_type: "food" | "grocery");
// shops already in that state are untouched.
export const closeAllShops = (data) => API.put("/vendors/close-all-shops", data);
export const openAllShops = (data) => API.put("/vendors/open-all-shops", data);
export const uploadVendorShopImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return API.post("/vendors/uploads/shop_image", fd);
};
// Onboarding document uploads (registration certificate, PAN, Aadhaar, GST
// certificate, cancelled cheque, address proof, agreement, FSSAI) — `type`
// is one of mahinzo_BE's uploadMiddleware.js vendor_* UPLOAD_CONFIG keys.
export const uploadVendorDocument = (type, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return API.post(`/vendors/uploads/${type}`, fd);
};

// CUSTOMER APIs (branch-scoped list + enable/disable — GET /api/user is
// mounted singular (routes/index.js), and already only ever returns
// role_id=5 customers, see userModel.findAll)
export const getCustomers = (params) => API.get("/user", { params });
export const toggleCustomerStatus = (id, data) => API.patch(`/user/${id}/status`, data);

// SELF PROFILE APIs (any logged-in user editing their own account — role excluded)
export const getMyProfile = () => API.get("/user/profile");
export const updateMyProfile = (data) => {
  const fd = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) fd.append(key, value);
  });
  return API.put("/user/profile", fd);
};

// STAFF APIs (unified for all roles)
export const getStaffs = (params) => API.get("/staff", { params });
export const getStaffById = (id) => API.get(`/staff/${id}`);
export const createStaff = (data) => API.put("/staff", data);
export const updateStaff = (id, data) => API.put(`/staff/${id}`, data);
export const approveRejectStaff = (id, data) => API.patch(`/staff/${id}/approval`, data);
export const toggleStaffStatus = (id, data) => API.patch(`/staff/${id}/status`, data);
export const changeStaffRole = (id, data) => API.patch(`/staff/${id}/role`, data);
export const deleteStaff = (id) => API.delete(`/staff/${id}`);
export const getStaffAttendanceList = (params) => API.get("/attendance/staff-list", { params });
export const getAttendanceCalendar = (userId, params) => API.get(`/attendance/calendar/${userId}`, { params });
export const uploadStaffProfile = (file) => { const fd = new FormData(); fd.append("file", file); return API.post("/staff/upload/profile", fd); };
export const uploadStaffLicense = (file) => { const fd = new FormData(); fd.append("file", file); return API.post("/staff/upload/license", fd); };
export const uploadStaffIdProof = (file) => { const fd = new FormData(); fd.append("file", file); return API.post("/staff/upload/idproof", fd); };

// VENDOR APPROVAL APIs
export const approveRejectVendor = (id, data) => API.patch(`/vendors/${id}/approval`, data);
export const toggleVendorStatus = (id, data) => API.patch(`/vendors/${id}/status`, data);
// PURCHASE PERSON APIs
export const getPurchasePersons = (params) => API.get("/purchase/persons", { params });
export const getActivePurchasePersons = (params) => API.get("/purchase/persons/active", { params });
export const getPurchasePersonById = (id) => API.get(`/purchase/persons/${id}`);
export const createPurchasePerson = (data) => API.put("/purchase/persons", data);
export const updatePurchasePerson = (id, data) => API.put(`/purchase/persons/${id}`, data);
export const approveRejectPurchasePerson = (id, data) => API.patch(`/purchase/persons/${id}/approval`, data);
export const togglePurchasePersonStatus = (id, data) => API.patch(`/purchase/persons/${id}/status`, data);
export const deletePurchasePerson = (id) => API.delete(`/purchase/persons/${id}`);
export const getPurchaseAttendance = (params) => API.get("/purchase/attendance", { params });
export const getMyPurchaseAttendance = (params) => API.get("/purchase/me/attendance", { params });
export const uploadPurchaseProfile = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return API.post("/purchase/upload/profile", fd);
};
export const uploadPurchaseLicense = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return API.post("/purchase/upload/purchase_license", fd);
};
export const uploadPurchaseIdProof = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return API.post("/purchase/upload/purchase_idproof", fd);
};

// QC INSPECTOR APIs
export const uploadQCImage = (files) => {
  const fd = new FormData();
  const arr = Array.isArray(files) ? files : [files];
  arr.forEach((f) => fd.append("files", f));
  return API.post("/qc/upload/qc_image", fd);
};
export const getQCInspectors = (params) => API.get("/qc/inspectors", { params });
export const getQCInspectorById = (id) => API.get(`/qc/inspectors/${id}`);
export const updateQCInspector = (id, data) => API.put(`/qc/inspectors/${id}`, data);
export const approveRejectQCInspector = (id, data) => API.patch(`/qc/inspectors/${id}/approval`, data);
export const toggleQCInspectorStatus = (id, data) => API.patch(`/qc/inspectors/${id}/status`, data);
export const deleteQCInspector = (id) => API.delete(`/qc/inspectors/${id}`);

// QC INSPECTIONS APIs (order quality-check workflow)
export const getQCInspections = (params) => API.get("/qc/inspections", { params });
export const getQCPendingInspections = (params) => API.get("/qc/pending", { params });
export const getQCInspectionById = (id) => API.get(`/qc/inspections/${id}`);
export const approveQCInspection = (id, data) => API.post(`/qc/inspections/${id}/approve`, data);

// DELIVERY BOY APIs
export const uploadDeliveryBoyProfile = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return API.post("/delivery-boy/upload/profile", fd);
};

export const uploadDeliveryBoyIdProof = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return API.post("/delivery-boy/upload/idproof", fd);
};

export const uploadDeliveryBoyLicense = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return API.post("/delivery-boy/upload/license", fd);
};

export const getDeliveryBoys = (params) => API.get("/delivery-boy", { params });
export const getActiveDeliveryBoys = (params) => API.get("/delivery-boy/active", { params });
export const getDeliveryBoyById = (userId) => API.get(`/delivery-boy/${userId}`);
export const createDeliveryBoy = (data) => API.put("/delivery-boy", data);
export const updateDeliveryBoy = (userId, data) => API.put(`/delivery-boy/${userId}`, data);
export const approveRejectDeliveryBoy = (userId, data) => API.patch(`/delivery-boy/${userId}/approval`, data);
export const toggleDeliveryBoyStatus = (userId, data) => API.patch(`/delivery-boy/${userId}/status`, data);
export const deleteDeliveryBoy = (userId) => API.delete(`/delivery-boy/${userId}`);
export const getDeliveryBoyAttendance = (userId, params) => API.get(`/delivery-boy/${userId}/attendance`, { params });
export const exportDeliveryBoysCSV = (params) =>
  API.get("/delivery-boy", { params: { ...params, export: "csv" } });

// PRODUCT APIs
export const getProducts = (params) => API.get("/product", { params });
export const getProductById = (id) => API.get(`/product/${id}`);
export const createProduct = (data) => API.put("/product", data);
export const updateProduct = (id, data) => API.put(`/product/${id}`, data);
export const deleteProduct = (id) => API.delete(`/product/${id}`);
export const uploadProductImage = (file) => {
  const fd = new FormData();
  fd.append("files", file);
  return API.post("/product/uploads/product", fd);
};
export const getProductsByShop = (params) => API.get("/product/by-shop", { params });
export const checkSimilarProducts = (params) => API.get("/product/similar-check", { params });
export const getProductVariants = (id) => API.get(`/product/${id}/variants`);
export const addProductVariant = (id, data) => API.post(`/product/${id}/variants`, data);
export const updateProductVariant = (variantId, data) => API.put(`/product/variants/${variantId}`, data);
export const deleteProductVariant = (variantId) => API.delete(`/product/variants/${variantId}`);
// Every vendor currently stocking one variant (their own cost + stock) —
// the real "who else sells this" list, distinct from getProductsByShop's
// name-matched duplicate-product search.
export const getVendorsForVariant = (variantId) => API.get(`/vendor-products/by-variant/${variantId}`);

// Reconciles a product's variant rows after an edit: deletes rows the user
// removed, updates rows that still carry a variant_id, and creates brand-new
// rows. (Create sends the whole list in one shot via createProduct; this is for
// the edit path, where the base product is updated separately.)
export const syncProductVariants = async (productId, variants = [], originalVariants = []) => {
  const keptIds = variants.map((v) => v.variant_id).filter(Boolean);
  const removed = originalVariants
    .map((v) => v.variant_id)
    .filter((id) => id && !keptIds.includes(id));

  // Create/update first, delete removed sizes LAST — previously the deletes
  // ran first, so a create that was then rejected (e.g. invalid margin) left
  // the product with its old size gone and no new one (confirmed live:
  // MA-PR02138 ended up with no sizes at all). A failure below now throws
  // before anything is deleted.
  for (const v of variants) {
    const payload = {
      variant_label: v.variant_label || "",
      price: v.price,
      offer_price: v.offer_price ?? 0,
      discount_percentage: v.discount_percentage !== "" && v.discount_percentage != null ? v.discount_percentage : null,
      stock_quantity: v.stock_quantity ?? 0,
      unit: v.unit,
      // Without this, a row that lost its variant_id (e.g. Base Price edit
      // regenerated every row) gets deleted and recreated via addVariant,
      // whose computePricing treats a missing margin_percent as an explicit
      // 0% rather than "inherit the product's default" — silently zeroing
      // out a margin that was set correctly moments earlier.
      margin_percent: v.margin_percent !== "" && v.margin_percent != null ? v.margin_percent : null,
    };
    if (v.variant_id) {
      await updateProductVariant(v.variant_id, payload);
    } else {
      await addProductVariant(productId, payload);
    }
  }

  await Promise.all(removed.map((id) => deleteProductVariant(id)));
};
export const exportProductsCSV = (params) =>
  API.get("/product", { params: { ...params, export: "csv" } });
export const searchProductsByShop = (params) =>
  API.get("/product/shops/search", { params });
export const getProductApprovals = (params) => API.get("/product/approvals", { params });
export const approveRejectProduct = (id, data) => API.patch(`/product/${id}/approval`, data);
// Merges a duplicate product (e.g. a second vendor's separately-created "Tomato")
// into an existing primary product. data: { primary_product_id, variant_mapping }.
export const mergeProduct = (id, data) => API.patch(`/product/${id}/merge`, data);
export const bulkUploadProducts = (files) => {
  const fd = new FormData();
  files.forEach((file) => fd.append("files", file));
  return API.post("/product/bulk-upload", fd);
};

// ORDER APIs
export const getOrders = (params) => API.get("/orders", { params });
export const getOrderById = (id) => API.get(`/orders/${id}`);
export const assignVendor = (id, vendor_id) => API.post(`/orders/${id}/assign-vendor`, { vendor_id });
export const assignDeliveryBoy = (id, delivery_boy_id) => API.post(`/orders/${id}/assign-delivery`, { delivery_boy_id });
// QC inspection for an order (null until it reaches the godown) + the
// inspectors in its branch that it can be (re)assigned to.
export const getOrderQcAssignment = (id) => API.get(`/orders/${id}/qc`);
export const assignQcInspector = (id, inspector_id) => API.post(`/orders/${id}/assign-qc`, { inspector_id });
// Admin-initiated cancellation (vendor-unavailable, out-of-stock, etc.) —
// distinct from the customer's own self-cancel; skips the 5-min window/COD/
// category-cancellable checks since an admin can override those.
export const cancelOrderByAdmin = (id, reason) => API.post(`/orders/${id}/cancel-vendor`, { reason });
// Sends a free-text delay update straight to the order's customer as a push
// notification — used when an order is running late (vendor prep, traffic,
// short-staffed delivery) so the reason can be worded per-case.
export const notifyOrderDelay = (id, message) => API.post(`/orders/${id}/notify-delay`, { message });
export const scanBarcode = (barcode) => API.get(`/orders/scan/${barcode}`);
export const printReceipt = (barcode) => API.post(`/orders/scan/${barcode}/print`);
export const getVendorBill = (orderId) => API.get(`/orders/${orderId}/vendor-bill`);
export const getCustomerBill = (orderId) => API.get(`/orders/${orderId}/customer-bill`);
export const getOrdersByUser = (user_id) => API.get(`/orders/user/${user_id}`);

// PURCHASE ASSIGNMENT APIs
export const getPurchaseAssignments = (params) => API.get("/purchase/assignments", { params });
export const acceptPurchaseAssignment = (orderId) => API.post(`/purchase/assignments/${orderId}/accept`);
export const rejectPurchaseAssignment = (orderId) => API.post(`/purchase/assignments/${orderId}/reject`);
// Manual (re)assign a pending/rejected purchase assignment to a chosen person.
// Allowed for Super Admin, Admin and Branch Manager (enforced server-side too).
export const manualAssignPurchase = (assignmentId, personId) =>
  API.post(`/purchase/assignments/${assignmentId}/assign`, { person_id: personId });

// EXPENSE APIs
export const getExpenses = (params) => API.get("/expenses", { params });
export const createExpense = (data) => API.post("/expenses", data);
export const updateExpense = (id, data) => API.put(`/expenses/${id}`, data);
export const deleteExpense = (id) => API.delete(`/expenses/${id}`);

// PAYMENT APIs
export const getPayments = (params) => API.get("/payments", { params });
export const getPaymentRefunds = (params) => API.get("/payments/refunds", { params });
export const exportPaymentsCSV = (params) =>
  API.get("/payments", { params: { ...params, export: "csv" }, responseType: "blob" });
export const exportRefundsCSV = (params) =>
  API.get("/payments/refunds", { params: { ...params, export: "csv" }, responseType: "blob" });
export const createPaymentOrder = (orderId) =>
  API.post("/payments/create-order", { order_id: orderId });
export const verifyPayment = (data) => API.post("/payments/verify", data);
export const paymentWebhook = (data) => API.post("/payments/webhook", data);

// PAYMENT RECOVERY APIs — captured payments that never produced an order
export const getPaymentRecovery = (params) => API.get("/payments/recovery", { params });
export const retryPaymentRecovery = (id) => API.post(`/payments/recovery/${id}/retry`);
export const refundPaymentRecovery = (id) => API.post(`/payments/recovery/${id}/refund`);

// APYHUB — Aadhaar validation (external API, separate axios instance)
export const validateAadhaarAPI = (aadhaarNumber) =>
  axios.post(
    "https://api.apyhub.com/validate/aadhaar",
    { aadhaar: aadhaarNumber },
    {
      headers: {
        "Content-Type": "application/json",
        "apy-token": process.env.REACT_APP_APYHUB_TOKEN,
      },
    }
  );

// DASHBOARD APIs
export const getDashboardOverview = (params) => API.get("/dashboard/overview", { params });
export const getDashboardBarChart = (params) => API.get("/dashboard/month-revenue", { params });
export const getDashboardRecentOrders = (params) => API.get("/dashboard/recent-orders", { params });

// REPORT APIs
export const getReportSummary = (params) => API.get("/reports/summary", { params });
export const getReportDetails = (params) => API.get("/reports/details", { params });
export const getReportOrderItems = (orderIdNum, params) => API.get(`/reports/${orderIdNum}/items`, { params });
// GST filing report — CSV file (opens in Excel) for the given from_date/to_date.
export const downloadGstReport = (params) => API.get("/reports/gst", { params, responseType: "blob" });

// RATING MANAGEMENT APIs — view + filter only, one endpoint per rating type
// (product/vendor/delivery partner are rated separately by the customer).
export const getProductRatings = (params) => API.get("/ratings/products", { params });
export const getVendorRatings = (params) => API.get("/ratings/vendors", { params });
export const getDeliveryRatings = (params) => API.get("/ratings/delivery-partners", { params });

// SETTLEMENT APIs
export const getSettlementPending = (params) => API.get("/settlement/pending", { params });
// Correcting the vendor-level "Pending" rollup itself (Super Admin/Admin only).
export const editSettlementPendingAmount = (vendorId, data) => API.put(`/settlement/${vendorId}/pending-amount`, data);
// Per-order, per-product vendor rate correction (Super Admin/Admin only).
export const getSettlementItems = (settlementId) => API.get(`/settlement/settlements/${settlementId}/items`);
export const editSettlementItems = (settlementId, data) => API.put(`/settlement/settlements/${settlementId}/items`, data);
export const getVendorWallet = (vendorId) => API.get(`/vendors/${vendorId}/wallet`);
export const getVendorWalletTransactions = (vendorId, params) => API.get(`/vendors/${vendorId}/wallet/transactions`, { params });

// Manual vendor payment workflow: Unpaid -> Approved -> Paid -> Confirmed.
export const getVendorSettlementTimeline = (vendorId) => API.get(`/settlement/${vendorId}/timeline`);
export const approvePaymentRequest = (vendorId, date) => API.post(`/settlement/payment-requests/${vendorId}/approve`, date ? { date } : {});
export const getPaymentRequests = (params) => API.get("/settlement/payment-requests", { params });
export const markPaymentDone = (requestId, data) => API.put(`/settlement/payment-requests/${requestId}/mark-paid`, data);
export const confirmPaymentRequest = (requestId) => API.put(`/settlement/payment-requests/${requestId}/confirm`);
// Payment screenshot — Superadmin only, uploaded before calling markPaymentDone.
export const uploadPaymentScreenshot = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return API.post("/settlement/uploads/receipt", fd);
};

// SALARY APIs
export const getSalaryStaffList = (params) => API.get("/salary/staff", { params });
export const creditStaffSalary = (data) => API.put("/salary/credit", data);
export const getSalaryCreditHistory = (params) => API.get("/salary/history", { params });